import {
  TileFontSizeVariant,
  tileWidthScaleForVariant,
} from '@/components/diagrams/components/Tile';
import { Node } from '@/components/diagrams/FlowChart';
import {
  createTextNoteNodeFromConfig,
  getTextNoteConfigs,
} from '@/components/diagrams/FlowChart/utils/textNoteUtils';
import {
  DiagramDispositionsFormState,
  NonRootRecipientOverride,
} from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { projectVisualizationGroups } from '@/modules/graphViz/graphVizGroup/grouping';
import { AfterDeath } from '@/types/schema';

import { DEFAULT_ENTITY_DIAGRAM_STATE } from '../contexts/entityDiagram.context';
import { createEntityDiagramGraph } from '../entityDiagramGraph/entityDiagramGraph';
import {
  extractReactFlowNodesAndEdges,
  getNodeId,
} from '../entityDiagramGraph/utils';
import { EntityDiagram_NodeConfigurationFragment } from '../graphql/EntityDiagram.generated';
import {
  EntityDiagramState,
  EntityDiagramStateProps,
  EntityDiagramVariant,
} from '../types';
import { applyNodeConfigurations } from '../utils/applyNodeConfigurations';
import { applyAutoLayoutToState } from './applyAutoLayoutToState';

const ROW_WIDTH = 650;
const SINGLE_ITEM_MAX_WIDTH = 220; // Reasonable max width for single items

export function generateEntityDiagramGraphState({
  primaryClients,
  entity,
  isTwoClientHousehold,
  isGeneratingDefaultState = false,
  selectedPrimaryClientId,
  entityDiagramVariant,
  nodeConfigurations,
  externalDispositions,
  recipientDispositions,
  nonRootRecipientOverride,
  isOnDispositionsFormDiagram,
  visualizationGroups,
  hideEntitiesListInGroups,
}: EntityDiagramStateProps & {
  isGeneratingDefaultState?: boolean;
  nodeConfigurations: EntityDiagram_NodeConfigurationFragment[];
  externalDispositions?: DiagramDispositionsFormState;
  nonRootRecipientOverride?: NonRootRecipientOverride | null;
}): Pick<
  EntityDiagramState,
  'entityDiagramGraph' | 'nodes' | 'edges' | 'baseEdges'
> {
  const entityDiagramGraph = createEntityDiagramGraph({
    grantors: primaryClients,
    entity,
    isTwoClientHousehold,
    isGeneratingDefaultState,
    firstGrantorDeathId: selectedPrimaryClientId,
    entityDiagramVariant,
    externalDispositions,
    recipientDispositions,
    nonRootRecipientOverride,
    isOnDispositionsFormDiagram,
  });

  const { nodes, edges } = extractReactFlowNodesAndEdges(entityDiagramGraph);

  // Apply saved node configurations if available
  const finalNodes = nodeConfigurations
    ? applyNodeConfigurations(nodes, nodeConfigurations)
    : nodes;

  // Reconstitute text note nodes from saved configurations
  const textNoteNodes: Node[] = nodeConfigurations
    ? getTextNoteConfigs(nodeConfigurations).map(createTextNoteNodeFromConfig)
    : [];

  const projected = projectVisualizationGroups({
    nodes: [...finalNodes, ...textNoteNodes],
    edges,
    groups: visualizationGroups,
    sectioned: true,
    hideEntitiesListInGroups,
  });

  const projectedGroupNodes = projected.nodes.filter(
    (node) => node.data.isGroupNode
  );
  const configuredGroupNodesById = new Map(
    applyNodeConfigurations(projectedGroupNodes, nodeConfigurations).map(
      (node) => [node.id, node]
    )
  );

  return {
    entityDiagramGraph,
    nodes: projected.nodes.map(
      (node) => configuredGroupNodesById.get(node.id) ?? node
    ),
    edges: projected.edges,
    baseEdges: edges,
  };
}

export function generateDefaultState(
  defaultStateProps: EntityDiagramStateProps
): EntityDiagramState {
  const entityDiagramGraphProps = generateEntityDiagramGraphState({
    ...defaultStateProps,
    isGeneratingDefaultState: true,
  });

  const state = {
    ...DEFAULT_ENTITY_DIAGRAM_STATE,
    ...defaultStateProps,
    ...entityDiagramGraphProps,
  };

  const { grid } = applyAutoLayoutToState(state);
  const flatGrid = grid.flat();

  state.nodes.forEach((node) => {
    // Only apply row-dependent width configuration for disposition diagrams
    if (
      state.entityDiagramVariant !== EntityDiagramVariant.Dispositions ||
      node.data.hiddenByVisualizationGroup
    ) {
      return;
    }

    const correspondingTileNode = flatGrid.find((n) => n.id === node.id);
    const correspondingGridRowSize = flatGrid.filter(
      (n) => n.position.y === correspondingTileNode?.position.y
    ).length;

    if (correspondingTileNode?.data.inheritWidth) {
      return;
    }

    // A positive numeric data.width marks a tile explicitly resized via
    // NodeResizer; keep it instead of overriding with the row-derived default.
    // Diagrams with no resized tiles are unaffected (their data.width is an
    // auto-width object).
    if (typeof node.data.width === 'number' && node.data.width > 0) {
      return;
    }

    const calculatedWidth = Math.min(
      ROW_WIDTH / correspondingGridRowSize,
      SINGLE_ITEM_MAX_WIDTH
    );

    // Scale the grid-derived width by any persisted fontSizeVariant so
    // tiles reload at the same size a user applied via the toolbar +/-.
    // applyNodeConfigurations set node.data.fontSizeVariant above; we undo
    // the grid's override by re-applying the same scale factor here.
    const scale = tileWidthScaleForVariant(
      node.data.fontSizeVariant as TileFontSizeVariant | undefined
    );

    // Always apply a reasonable max width to prevent oversized tiles
    node.data.width = {
      width: 'auto',
      minWidth: calculatedWidth * scale,
      maxWidth: calculatedWidth * scale,
    };
  });

  // If a selected node is provided, highlight it in the diagram
  if (defaultStateProps.initialSelectedNode) {
    const node = state.nodes.find(
      (n) =>
        n.id ===
        getNodeId({
          id: defaultStateProps.initialSelectedNode?.pulid ?? '',
          afterDeath:
            defaultStateProps.initialSelectedNode?.afterDeath ??
            AfterDeath.None,
        })
    );
    if (node) {
      node.selected = true;
    }
  }

  return state;
}
